import clsx from 'clsx';
import { ArrowRight, Lightbulb, RotateCcw, Sparkles, Undo2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, newKey } from '../../lib/api';
import type { Answer, Feedback, ItemState, PublicItem } from '../../lib/types';
import { playVoice, speak, stopVoice } from '../../lib/voice';
import { Button, Notice, ReadAloud, Stars } from '../../ui';

export interface QuestionMedia {
  voice?: { question?: string; hint?: string; correct?: string; nudge?: string };
  hintVideo?: string;
  workedVideo?: string;
}

interface Props {
  item: PublicItem;
  attemptId: string;
  initial: ItemState;
  /** Called once the item is finished (correct or out of tries). */
  onFinished: (stars: number) => void;
  continueLabel?: string;
  compact?: boolean;
  media?: QuestionMedia;
  /** Read the question out as soon as it appears (video checkpoints). */
  autoRead?: boolean;
}

const NUDGE_AFTER_MS = 25_000;

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function QuestionCard({ item, attemptId, initial, onFinished, continueLabel = 'Continue', compact, media, autoRead }: Props) {
  const voice = media?.voice;
  const readText = [item.prompt, ...(item.options ?? []), ...(item.cards ?? [])].join('. ');
  const [tries, setTries] = useState(initial.tries);
  const [feedback, setFeedback] = useState<Feedback | null>(
    initial.done ? { correct: initial.stars >= 2, final: true, stars: initial.stars, hint: null, workedExample: null, answer: null, replayed: true } : null,
  );
  const [choice, setChoice] = useState<number | null>(null);
  const [sequence, setSequence] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Same key for retries of the same try, so a timeout + retry cannot double count.
  const pendingKey = useRef<string | null>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  // On small screens the feedback and next button can sit below the fold.
  useEffect(() => {
    if (!feedback || feedback.replayed) return;
    const t = setTimeout(() => actionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 250);
    return () => clearTimeout(t);
  }, [feedback]);

  // Narrator: read the question when it opens; stop talking when it closes.
  useEffect(() => {
    if (autoRead && !initial.done) void playVoice(voice?.question, readText);
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A gentle nudge if the child has not tried anything for a while.
  const touched = choice !== null || sequence.length > 0 || text !== '' || !!feedback;
  useEffect(() => {
    if (touched || initial.done || !voice?.nudge) return;
    const t = setTimeout(() => void playVoice(voice.nudge), NUDGE_AFTER_MS);
    return () => clearTimeout(t);
  }, [touched, initial.done, voice?.nudge]);

  // Speak the outcome. The worked-example clip has its own narration.
  useEffect(() => {
    if (!feedback || feedback.replayed) return;
    if (feedback.correct) void playVoice(voice?.correct, feedback.stars === 3 ? 'Brilliant! You got it first time.' : 'You got it!');
    else if (!feedback.final) void playVoice(voice?.hint, feedback.hint ?? undefined);
    else if (!media?.workedVideo) void speak(feedback.workedExample ?? undefined);
    else stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const answer: Answer | null = useMemo(() => {
    if (item.kind === 'choice') return choice === null ? null : { choice };
    if (item.kind === 'sequence') return sequence.length === (item.cards?.length ?? 0) ? { sequence: sequence.join(' > ') } : null;
    return text.trim() ? { text: text.trim() } : null;
  }, [item, choice, sequence, text]);

  const finished = feedback?.final ?? false;
  const locked = busy || finished;

  const submit = async () => {
    if (!answer) return;
    setError('');
    setBusy(true);
    pendingKey.current ??= newKey();
    try {
      const fb = await api.post<Feedback>(`/student/attempts/${attemptId}/responses`, {
        idempotencyKey: pendingKey.current,
        itemId: item.id,
        answer,
        tryNo: tries + 1,
      });
      pendingKey.current = null;
      setTries((t) => t + 1);
      setFeedback(fb);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TRY_OUT_OF_ORDER') {
        pendingKey.current = null;
        setTries(Number(e.details?.expectedTry ?? 1) - 1);
        setError('Let’s try that once more.');
      } else if (e instanceof ApiError && e.code === 'ITEM_ALREADY_DONE') {
        onFinished(0);
      } else {
        setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setFeedback(null);
    setChoice(null);
    setSequence([]);
    setText('');
  };

  return (
    <div className={clsx('flex flex-col', compact ? 'gap-4' : 'gap-6')}>
      <div className="flex items-start gap-3">
        <h2 className={clsx('flex-1 font-display font-bold leading-snug', compact ? 'text-xl' : 'text-2xl sm:text-3xl')}>{item.prompt}</h2>
        <ReadAloud text={readText} src={voice?.question} />
      </div>

      {item.kind === 'choice' && (
        <div className={clsx('grid gap-3', (item.options?.length ?? 0) > 3 ? 'sm:grid-cols-2' : '')}>
          {item.options?.map((opt, i) => (
            <button
              key={i}
              type="button"
              disabled={locked}
              onClick={() => setChoice(i)}
              aria-pressed={choice === i}
              className={clsx(
                'flex min-h-16 items-center gap-4 rounded-2xl border-2 px-4 text-left text-lg font-semibold transition-all',
                choice === i ? 'border-brand bg-brand-soft text-brand-strong' : 'border-line bg-surface hover:border-brand/60',
                locked && 'cursor-default',
              )}
            >
              <span className={clsx('flex size-9 shrink-0 items-center justify-center rounded-xl font-display font-extrabold', choice === i ? 'bg-brand text-white' : 'bg-canvas text-ink-soft')}>
                {LETTERS[i]}
              </span>
              {opt}
            </button>
          ))}
        </div>
      )}

      {item.kind === 'sequence' && (
        <div className="flex flex-col gap-4">
          <ol className="grid gap-2">
            {item.cards?.map((_, i) => (
              <li key={i} className="flex min-h-14 items-center gap-3 rounded-2xl border-2 border-dashed border-line-strong bg-canvas px-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-soft font-display font-bold text-brand">{i + 1}</span>
                {sequence[i] ? (
                  <button type="button" disabled={locked} onClick={() => setSequence((s) => s.slice(0, i))} className="flex-1 text-left text-lg font-semibold">
                    {sequence[i]}
                  </button>
                ) : (
                  <span className="text-muted">Tap a card below</span>
                )}
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-2">
            {item.cards?.filter((c) => !sequence.includes(c)).map((c) => (
              <motion.button
                layout
                key={c}
                type="button"
                disabled={locked}
                onClick={() => setSequence((s) => [...s, c])}
                className="min-h-14 rounded-2xl border-2 border-brand/30 bg-surface px-5 text-lg font-semibold shadow-sm hover:border-brand"
              >
                {c}
              </motion.button>
            ))}
            {sequence.length > 0 && !locked && (
              <Button variant="ghost" onClick={() => setSequence([])} icon={<Undo2 className="size-4" aria-hidden />}>
                Start over
              </Button>
            )}
          </div>
        </div>
      )}

      {item.kind === 'text' && (
        <input
          aria-label="Your answer"
          value={text}
          disabled={locked}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && answer && !locked && void submit()}
          inputMode="text"
          autoComplete="off"
          className="h-16 w-full max-w-xs rounded-2xl border-2 border-line bg-surface px-5 font-display text-2xl font-bold outline-none focus:border-brand"
        />
      )}

      {error && <Notice tone="warn">{error}</Notice>}

      <AnimatePresence mode="wait">
        {feedback && <FeedbackPanel key={`${tries}-${feedback.final}`} fb={feedback} media={media} />}
      </AnimatePresence>

      <div ref={actionsRef} className="flex flex-wrap gap-3 scroll-mb-6">
        {!feedback && (
          <Button size="lg" onClick={() => void submit()} disabled={!answer} loading={busy}>
            Check my answer
          </Button>
        )}
        {feedback && !feedback.final && (
          <Button size="lg" variant="gold" onClick={retry} icon={<RotateCcw className="size-5" aria-hidden />}>
            Try again
          </Button>
        )}
        {finished && (
          <Button size="lg" onClick={() => onFinished(feedback?.stars ?? 0)} icon={<ArrowRight className="size-5" aria-hidden />}>
            {continueLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

function FeedbackPanel({ fb, media }: { fb: Feedback; media?: QuestionMedia }) {
  const clip = !fb.correct && !fb.replayed ? (fb.final ? media?.workedVideo : media?.hintVideo) : undefined;
  const tone = fb.correct ? 'bg-mint-soft text-[#0c6b52]' : fb.final ? 'bg-brand-soft text-brand-strong' : 'bg-gold-soft text-[#7a4f05]';
  const title = fb.correct
    ? fb.stars === 3 ? 'Brilliant! You got it first time.' : 'You got it!'
    : fb.final ? 'Let’s work it out together' : 'Not quite yet. Here’s a clue.';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className={clsx('flex flex-col gap-2 rounded-2xl p-5', tone)}
      role="status"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          {fb.correct ? <Sparkles className="size-5" aria-hidden /> : <Lightbulb className="size-5" aria-hidden />}
          {title}
        </p>
        {fb.final && (
          <motion.span initial={{ scale: 0.4, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 500, damping: 14 }}>
            <Stars earned={fb.stars} />
          </motion.span>
        )}
      </div>
      {clip && (
        <video
          src={clip}
          autoPlay
          playsInline
          controls
          className="mt-1 aspect-video w-full rounded-xl bg-ink"
          aria-label={fb.final ? 'Worked example video' : 'Hint video'}
        />
      )}
      {fb.hint && <p className="text-base">{fb.hint}</p>}
      {fb.workedExample && <p className="text-base">{fb.workedExample}</p>}
      {fb.final && !fb.correct && fb.answer && (
        <p className="text-base">
          The answer is <strong>{fb.answer}</strong>.
        </p>
      )}
    </motion.div>
  );
}
